import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { queryKeys, STEP_POLL_INTERVAL_MS } from '@/config';
import { apiClient, apiErrorMessage } from '@/lib';
import type { WorkflowInstanceStep } from '@/types';
import { invalidateStepQueries } from './step-queries';

/** "Dodela" (TASK-48): a head of sector picks who executes a step and when. */
export interface AssignStepPayload {
  userId: string;
  plannedStartAt?: string | null;
  dueAt?: string | null;
  sessions?: number | null;
  alsoStepIds?: string[];
}

export interface AssignCandidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface AssignCandidates {
  candidates: AssignCandidate[];
  /** Later unstaffed steps of the same role the same person may take at once. */
  laterSteps: { id: string; stepTemplateName: string; sortOrder: number }[];
}

export const stepAssignApi = {
  toAssign: async () => {
    const { data } = await apiClient.get<WorkflowInstanceStep[]>('/workflow/steps/to-assign');
    return data;
  },
  candidates: async (stepId: string) => {
    const { data } = await apiClient.get<AssignCandidates>(
      `/workflow/steps/${stepId}/assign-candidates`
    );
    return data;
  },
  assign: async (stepId: string, payload: AssignStepPayload) => {
    const { data } = await apiClient.post<WorkflowInstanceStep[]>(
      `/workflow/steps/${stepId}/assign`,
      payload
    );
    return data;
  },
};

export const TO_ASSIGN_KEY = [...queryKeys.tasks.all, 'to-assign'] as const;

export function useStepsToAssign(enabled = true) {
  return useQuery({
    queryKey: TO_ASSIGN_KEY,
    queryFn: stepAssignApi.toAssign,
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: STEP_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useAssignCandidates(stepId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.tasks.all, 'assign-candidates', stepId],
    queryFn: () => stepAssignApi.candidates(stepId!),
    enabled: !!stepId,
  });
}

export function useAssignStep() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: ({ stepId, payload }: { stepId: string; payload: AssignStepPayload }) =>
      stepAssignApi.assign(stepId, payload),
    onSuccess: () => {
      invalidateStepQueries(queryClient);
      toast({ title: t('assignStep.done'), variant: 'success' });
    },
    onError: (error) => {
      invalidateStepQueries(queryClient);
      toast({ title: apiErrorMessage(error, t('assignStep.failed')), variant: 'error' });
    },
  });
}
